import React from "react"
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCOPicture from "../../fundamentals/CCOPicture";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import CCOLinkable from "../../fundamentals/CCOLinkable";
import CCOHeadlineComponentHead from "../../fundamentals/CCOHeadlineComponentHead";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import {metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from "../../../context/PreviewContext";
import {buttonStyle} from "../../../common/constants/buttonStyle";
import {SosContactType} from "../../types";
import useWindowSize from "../../../utility/useWindowSize";
import {useThemeContext} from "../../../theming/ThemeContext";
import {useTheme} from "@mui/material/styles";


export interface Props {
    contactTeaser: SosContactType
    isSmall?: boolean
}

function C022ContactTeaser(props: Props) {
    const isPreview = usePreviewContext().isPreview;
    const {isWindowExtraSmall} = useWindowSize();
    const {themeMode} = useThemeContext();
    const theme = useTheme();
    const ctaButtonSx = {
        ...buttonStyle.green,
        ':hover': {
            ...buttonStyle.green[':hover'],
            ...(themeMode === 'testament' ? {backgroundColor: theme.palette.primary.light} : {}),
        }
    };
    const showCTAButton = !props.contactTeaser.callToActionSettings?.callToActionDisabled &&
                          props.contactTeaser.callToActionSettings?.callToActionCustomText &&
                          props.contactTeaser.teaserTarget?.selfUrl;

    function getBox() {
        return (
            <Box sx={{
                backgroundColor: 'primary.contrastText',
                marginTop: {xs: 2, md: 5},
                padding: 0,
            }}>
                {props.contactTeaser.picture ? <CCOPicture name={props.contactTeaser.picture?.name}
                                                           picture={props.contactTeaser.picture}
                                                           orientation={"landscape_ratio16x9"}
                                                           width={"1440"}
                                                           height={"810"}/> : null}
                {props.contactTeaser.detailText && props.contactTeaser.detailText.textAsTree ?
                    // Variant with information in "Freitext"
                    <Box
                        sx={{
                            paddingTop: {xs: 6, sm: 6, md: 8},
                            paddingX: {xs: 4, sm: 4, md: 6},
                            paddingBottom: {xs: 4, sm: 4, md: 6}
                        }}>
                        <CCORichText metadataId={props.contactTeaser.id}
                                     textAsTree={props.contactTeaser.detailText.textAsTree}
                                     propertyName='properties.detailText'
                                     collapseOnDoubleBreak={true}></CCORichText>
                    </Box>
                    :
                    // Variant with information in other fields
                    (props.contactTeaser.contactName || props.contactTeaser.address ?
                        <Box
                            sx={{
                                paddingTop: {xs: 6, sm: 6, md: 8},
                                paddingX: {xs: 4, sm: 4, md: 6},
                                paddingBottom: {xs: 4, sm: 4, md: 6}
                            }}>
                            {props.contactTeaser.institutionTitleTaxonomy &&
                            props.contactTeaser.institutionTitleTaxonomy.length > 0 ?
                                <Typography
                                    variant={'body1'}
                                    {...metaDataProperty(isPreview,
                                        'properties.name',
                                        props.contactTeaser.institutionTitleTaxonomy[0]!.id)}
                                    sx={{
                                        fontWeight: 'bold'
                                    }}>
                                    {props.contactTeaser.institutionTitleTaxonomy[0]!.name}
                                </Typography> : null}
                            <Typography variant={'body1'} {...metaDataProperty(isPreview,
                                'properties.jobTitle',
                                props.contactTeaser.id)}>{props.contactTeaser.jobTitle}</Typography>
                            <Typography
                                variant={'body1'}
                                {...metaDataProperty(isPreview,
                                    'properties.contactName',
                                    props.contactTeaser.id)}
                                sx={{
                                    fontWeight: 'bold'
                                }}>{props.contactTeaser.contactName}</Typography>
                            <Typography variant={'body1'} {...metaDataProperty(isPreview,
                                'properties.fieldOfWork',
                                props.contactTeaser.id)}>{props.contactTeaser.fieldOfWork}</Typography>
                            {props.contactTeaser.address && props.contactTeaser.address.textAsTree ?
                                <Box sx={{
                                    marginTop: 6
                                }}>
                                    <CCORichText metadataId={props.contactTeaser.id}
                                                 textAsTree={props.contactTeaser.address.textAsTree!}
                                                 propertyName='properties.address'
                                                 collapseOnDoubleBreak={true}></CCORichText></Box> :
                                null}
                            {props.contactTeaser.phone ? <React.Fragment>
                                <Typography variant={'body1'} sx={{
                                    display: "inline"
                                }}>
                                    {"Telefon: "}
                                </Typography>
                                <Link id={"link-tel-" + props.contactTeaser.id}
                                      href={"tel:" + props.contactTeaser.phone} underline="hover"
                                      variant={"linkLabel"}>
                                    {props.contactTeaser.phone}
                                </Link>
                                <br/>
                            </React.Fragment> : null}
                            {props.contactTeaser.mobile ? <React.Fragment>
                                <Typography variant={'body1'} sx={{
                                    display: "inline"
                                }}>
                                    {"Mobil: "}
                                </Typography>
                                <Link id={"link-mobile-" + props.contactTeaser.id}
                                      href={"tel:" + props.contactTeaser.mobile} underline="hover"
                                      variant={"linkLabel"}>
                                    {props.contactTeaser.mobile}
                                </Link>
                                <br/>
                            </React.Fragment> : null}
                            {props.contactTeaser.fax ? <Typography
                                    variant={'body1'} {...metaDataProperty(isPreview, 'properties.fax',
                                    props.contactTeaser.id)}>Fax: {props.contactTeaser.fax}</Typography> :
                                null}
                            {props.contactTeaser.contactEmail ?
                                <Link id={"link-contactmail-" + props.contactTeaser.id}
                                      href={"mailto:" + props.contactTeaser.contactEmail}
                                      underline="hover"
                                      variant={"linkLabel"}>
                                    {props.contactTeaser.contactEmail}
                                </Link>
                                : null}
                            {props.contactTeaser.additionalInfo &&
                            props.contactTeaser.additionalInfo.textAsTree ? <Box sx={{
                                marginTop: 6
                            }}>
                                    <CCORichText metadataId={props.contactTeaser.id}
                                                 textAsTree={props.contactTeaser.additionalInfo.textAsTree!}
                                                 propertyName='properties.additionalInfo'
                                                 collapseOnDoubleBreak={true}></CCORichText><br/></Box> :
                                <React.Fragment/>}
                        </Box> : null)
                }


            </Box>
        );
    }

    function getBoxInWrapper() {
        return <CCOComponentWrapper>
            <CCOContainer width='large'>
                {getBox()}
            </CCOContainer>
        </CCOComponentWrapper>
    }

    return (
        <CCOComponentWrapper background={"grey"}>
            <CCOContainer id={props.contactTeaser.id} background={"grey"} width={isWindowExtraSmall ? 'large' : 'small'} enableSmGrid>
                <CCOHeadlineComponentHead metadataId={props.contactTeaser.id}
                                          propertyName='properties.teaserTitle'
                                          subtextPropertyName='properties.subline'
                                          type={"componenthead"}
                                          headline={props.contactTeaser.teaserTitle}
                                          subtext={props.contactTeaser.subline?.textAsTree ??
                                              undefined}
                />
                {props.contactTeaser.picture || props.contactTeaser.detailText ||
                props.contactTeaser.contactName ?
                    props.isSmall ? getBoxInWrapper() : getBox() : <React.Fragment/>}
                {showCTAButton ?
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: {lg: 12, xs: 8}
                        }}>
                        <Button component={CCOLinkable} target={(props.contactTeaser as any)}
                                variant="contained" sx={ctaButtonSx}>
                            {(props.contactTeaser as any).callToActionSettings!.callToActionCustomText ??
                                ''}
                        </Button>
                    </Box> : <React.Fragment/>}
            </CCOContainer>
        </CCOComponentWrapper>
    );
}

export default C022ContactTeaser;